import { apiFetch } from "@/lib/api";

export type SupplierContact = {
  name?: string | null;
  position?: string | null;
  email?: string | null;
  telephone?: string | null;
  is_primary?: boolean | null;
};

export type SupplierCategory = {
  id: string;
  name: string;
  description?: string | null;
  supplier_count: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type Supplier = {
  id: number;
  name: string;
  country?: string | null;
  address?: string | null;
  website?: string | null;
  notes?: string | null;
  category_ids?: string[];
  categories?: SupplierCategory[];
  contacts?: SupplierContact[];
  primary_contact?: SupplierContact | null;
  contract_count: number;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type SupplierSummary = {
  id: number;
  name: string;
  country?: string | null;
  website?: string | null;
  categories?: SupplierCategory[];
  primary_contact?: SupplierContact | null;
  contract_count: number;
  updated_at?: string | null;
};

export type SupplierListResponse = {
  items: SupplierSummary[];
  total: number;
  countries: string[];
};

/** Matches ContractAlertThreshold on the backend. Ordered from earliest warning to most urgent. */
export type ContractAlertThreshold = "one_month" | "two_weeks" | "one_week" | "three_days" | "expired";

export type SupplierContract = {
  id: string;
  supplier_id: number;
  supplier_name?: string | null;
  title?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  start_date?: string | null;
  expiry_date?: string | null;
  days_until_expiry?: number | null;
  alert_threshold?: ContractAlertThreshold | null;
  archived: boolean;
  notes?: string | null;
  uploaded_by?: string | null;
  uploaded_by_name?: string | null;
  uploaded_at?: string | null;
  updated_at?: string | null;
  download_url: string;
  view_url: string;
};

export type ContractAlertResponse = {
  items: SupplierContract[];
  total: number;
};

export type SupplierPayload = {
  name: string;
  country?: string | null;
  address?: string | null;
  website?: string | null;
  notes?: string | null;
  category_ids?: string[];
  contacts?: SupplierContact[];
};

export const listSuppliers = async (params: {
  search?: string;
  country?: string;
  categoryId?: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortOrder?: string;
} = {}): Promise<SupplierListResponse> => {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.country) query.set("country", params.country);
  if (params.categoryId) query.set("categoryId", params.categoryId);
  if (params.page) query.set("page", String(params.page));
  if (params.size) query.set("size", String(params.size));
  if (params.sortField) query.set("sortField", params.sortField);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  const suffix = query.toString();
  return apiFetch<SupplierListResponse>(`/suppliers${suffix ? `?${suffix}` : ""}`);
};

export const getSupplier = async (id: number): Promise<Supplier> =>
  apiFetch<Supplier>(`/suppliers/${id}`);

export const createSupplier = async (payload: SupplierPayload): Promise<Supplier> =>
  apiFetch<Supplier>("/suppliers", { method: "POST", body: JSON.stringify(payload) });

export const updateSupplier = async (id: number, payload: SupplierPayload): Promise<Supplier> =>
  apiFetch<Supplier>(`/suppliers/${id}`, { method: "PUT", body: JSON.stringify(payload) });

/** CEO / General Manager only, and refused while the supplier still has contracts on file. */
export const deleteSupplier = async (id: number): Promise<void> => {
  await apiFetch<void>(`/suppliers/${id}`, { method: "DELETE" });
};

export const listSupplierCategories = async (): Promise<SupplierCategory[]> =>
  apiFetch<SupplierCategory[]>("/supplier-categories");

export const createSupplierCategory = async (payload: { name: string; description?: string | null }): Promise<SupplierCategory> =>
  apiFetch<SupplierCategory>("/supplier-categories", { method: "POST", body: JSON.stringify(payload) });

export const updateSupplierCategory = async (
  id: string,
  payload: { name: string; description?: string | null },
): Promise<SupplierCategory> =>
  apiFetch<SupplierCategory>(`/supplier-categories/${id}`, { method: "PUT", body: JSON.stringify(payload) });

/** `detach` unassigns the category from every supplier first; without it, an in-use category is refused. */
export const deleteSupplierCategory = async (id: string, detach = false): Promise<void> => {
  await apiFetch<void>(`/supplier-categories/${id}${detach ? "?detach=true" : ""}`, { method: "DELETE" });
};

export const listSupplierContracts = async (supplierId: number): Promise<SupplierContract[]> =>
  apiFetch<SupplierContract[]>(`/suppliers/${supplierId}/contracts`);

export const uploadSupplierContract = async (
  supplierId: number,
  file: File,
  meta: { title?: string; startDate?: string | null; expiryDate?: string | null; notes?: string | null },
): Promise<SupplierContract> => {
  const formData = new FormData();
  formData.append("file", file);
  if (meta.title) formData.append("title", meta.title);
  if (meta.startDate) formData.append("start_date", meta.startDate);
  if (meta.expiryDate) formData.append("expiry_date", meta.expiryDate);
  if (meta.notes) formData.append("notes", meta.notes);
  return apiFetch<SupplierContract>(`/suppliers/${supplierId}/contracts`, { method: "POST", body: formData });
};

/**
 * Full replacement of the contract's metadata: every field is written as sent, so omitting one
 * clears it. Archiving is a separate call on purpose -- see setSupplierContractArchived.
 */
export const updateSupplierContract = async (
  contractId: string,
  payload: {
    title?: string | null;
    start_date?: string | null;
    expiry_date?: string | null;
    notes?: string | null;
  },
): Promise<SupplierContract> =>
  apiFetch<SupplierContract>(`/supplier-contracts/${contractId}`, { method: "PUT", body: JSON.stringify(payload) });

/** Archived contracts stay on file but stop raising expiry alerts. */
export const setSupplierContractArchived = async (
  contractId: string,
  archived: boolean,
): Promise<SupplierContract> =>
  apiFetch<SupplierContract>(
    `/supplier-contracts/${contractId}/${archived ? "archive" : "unarchive"}`,
    { method: "POST" },
  );

export const deleteSupplierContract = async (contractId: string): Promise<void> => {
  await apiFetch<void>(`/supplier-contracts/${contractId}`, { method: "DELETE" });
};

/** Only what the current CEO/Manager has not dismissed yet at the contract's current threshold. */
export const listPendingContractAlerts = async (): Promise<ContractAlertResponse> =>
  apiFetch<ContractAlertResponse>("/supplier-contracts/alerts/pending");

/** Everything inside an alert window, dismissed or not. */
export const listContractAlerts = async (): Promise<ContractAlertResponse> =>
  apiFetch<ContractAlertResponse>("/supplier-contracts/alerts");

export const dismissContractAlerts = async (contractIds: string[]): Promise<void> => {
  await apiFetch<void>("/supplier-contracts/alerts/dismiss", {
    method: "POST",
    body: JSON.stringify({ contract_ids: contractIds }),
  });
};

export const CONTRACT_ALERT_LABELS: Record<ContractAlertThreshold, string> = {
  one_month: "1 month left",
  two_weeks: "2 weeks left",
  one_week: "1 week left",
  three_days: "3 days left",
  expired: "Expired",
};

/** Escalating colour ramp so urgency reads at a glance. */
export const CONTRACT_ALERT_TONES: Record<ContractAlertThreshold, string> = {
  one_month: "border-blue-300 bg-blue-50 text-blue-800",
  two_weeks: "border-amber-300 bg-amber-50 text-amber-800",
  one_week: "border-orange-300 bg-orange-50 text-orange-800",
  three_days: "border-rose-300 bg-rose-50 text-rose-800",
  expired: "border-red-400 bg-red-100 text-red-900",
};

export const describeExpiry = (contract: SupplierContract): string => {
  const days = contract.days_until_expiry;
  if (days === null || days === undefined) return "No expiry date";
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "Expires today";
  return `Expires in ${days} day${days === 1 ? "" : "s"}`;
};
